import { supabase } from "@/lib/supabase";
import { withTimeout } from "@/lib/timeout";

/**
 * Password management — email-link reset only.
 *
 * We do NOT expose a direct "supabase.auth.updateUser({ password })" call
 * from the client. That pattern lets anyone with access to a signed-in
 * session change the password, defeating the audit trail that proves the
 * legitimate user authorized the change. Email-link reset binds the change
 * to inbox control, which is a meaningfully stronger second factor than
 * "physical proximity to a signed-in laptop".
 *
 * sendResetEmail({ userId? }) invokes the `send-password-reset` edge
 * function which mints a recovery link via admin.generateLink and sends it
 * through Resend. Two modes:
 *   - userId omitted: self-service (uses caller's JWT email)
 *   - userId set: admin reset for that target user
 * Server enforces the admin gate + same-team check; passing a userId as a
 * non-admin returns 403.
 */

export interface SendResetResult {
  sent: true;
  /** Server returns the recipient masked, e.g. "ja***@example.com". */
  email: string;
}

export async function sendResetEmail(input: { userId?: string; email?: string } = {}): Promise<SendResetResult> {
  // Three modes:
  //   - {}                : self-service (uses caller JWT email)
  //   - { userId }        : admin-initiated for a teammate
  //   - { email }         : unauthed pre-login forgot-password
  const body: Record<string, unknown> =
    input.userId ? { user_id: input.userId } : input.email ? { email: input.email } : {};
  const { data, error } = await withTimeout(
    supabase.functions.invoke<SendResetResult & { error?: string; detail?: string }>(
      "send-password-reset",
      { body },
    ),
    20_000,
    "Password reset",
  );
  if (error) {
    // The error.context here is a Response when the function returned non-2xx.
    // Try to extract the structured error body to give the user a real message
    // instead of "Edge Function returned a non-2xx status code".
    const ctx = (error as { context?: { json?: () => Promise<{ error?: string; detail?: string }> } }).context;
    if (ctx?.json) {
      try {
        const body = await ctx.json();
        const msg = friendlyError(body?.error) ?? body?.detail;
        if (msg) throw new Error(msg);
      } catch (e) {
        if (e instanceof Error && e.message) throw e;
        // fall through to generic
      }
    }
    throw new Error(error.message || "Reset email failed");
  }
  if (!data || !data.sent) {
    throw new Error(friendlyError(data?.error) ?? data?.detail ?? "Reset email failed");
  }
  return data;
}

function friendlyError(code: string | undefined): string | undefined {
  if (!code) return undefined;
  switch (code) {
    case "admin_only": return "Admins only can reset another user's password";
    case "cross_tenant_blocked": return "You can only reset members of your own team";
    case "target_not_found": return "User not found";
    case "target_inactive": return "Reactivate this member first before sending a reset link";
    case "resend_not_configured": return "Email provider is not configured. Contact support.";
    case "supabase_not_configured": return "Server is misconfigured. Contact support.";
    case "missing_jwt":
    case "invalid_jwt": return "Your session expired. Sign in again.";
    case "link_failed": return "Could not generate a reset link. Try again.";
    case "resend_failed": return "Email provider rejected the message. Try again in a moment.";
    default: return code;
  }
}
